import { Banner, Box, Button, Icon, InlineStack, Spinner, TextField } from '@customerio/pluma-components/react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useWatch } from '@/components/form';
import { useVerifyWebhook } from '@/services/db/webhook/mutations';
import { getApiClientErrorDetailFromError } from '@/services/api/errors';
import { isEmptyString } from '@/utils/string';
import { useReportingWebhookFormAnalytics } from '../../analytics';
import type { ReportingWebhookFormApi } from '../types';

const DUPLICATE_ENDPOINT_NOTE =
  'Note: That endpoint URL is in use by another webhook. Using it again may result in duplicate events.';

type VerifyStatusProps = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
};

function VerifyStatus({ isPending, isSuccess, isError }: VerifyStatusProps) {
  if (isPending) return <Spinner />;
  if (isSuccess) return <Icon name="check-circle" color="success" />;
  if (isError) return <Icon name="critical" color="critical" />;
  return null;
}

type WebhookEndpointFieldProps = {
  form: ReportingWebhookFormApi;
  /** Signed into the test request. Empty for a webhook that has not been created yet. */
  signingKey: string;
  /** Named in the test-sent event. Empty on create, where no webhook exists yet. */
  webhookId: string;
  isDisabled: boolean;
  /** Another webhook already points at this endpoint. */
  hasDuplicateEndpoint: boolean;
};

/**
 * The endpoint URL plus its "Send test" action.
 *
 * The value is trimmed as it is typed, not only on submit, because everything downstream reads the
 * live field: the test request, the duplicate check, and the save-enabled rule all need the URL a
 * paste actually produced rather than one padded with whitespace.
 */
export function WebhookEndpointField({
  form,
  signingKey,
  webhookId,
  isDisabled,
  hasDuplicateEndpoint,
}: WebhookEndpointFieldProps) {
  const snackbars = useSnackbars();
  const verifyWebhook = useVerifyWebhook();
  const analytics = useReportingWebhookFormAnalytics();
  const endpointUrl = useWatch({ control: form.control, name: 'endpoint_url' });
  const withContent = useWatch({ control: form.control, name: 'with_content' });
  const error = form.formState.errors.endpoint_url?.message;

  const setEndpointUrl = (value: string) => {
    form.setValue('endpoint_url', value.trim(), { shouldValidate: true, shouldDirty: true, shouldTouch: true });
  };

  const isSendTestDisabled = error != null || isEmptyString(endpointUrl) || verifyWebhook.isPending || isDisabled;

  const sendTest = async () => {
    try {
      await verifyWebhook.mutateAsync({
        endpoint: endpointUrl,
        signing_key: signingKey,
        with_content: withContent,
      });
      snackbars.create({
        variant: 'default',
        id: 'verify.reporting-webhook.success',
        message: 'We sent a message to your webhook endpoint.',
      });
      void analytics.trackTestSent(webhookId, { passed: true });
    } catch (error) {
      // A failed test is reported by the icon beside the button, not by a snackbar — but the
      // server's reason still goes to analytics, which is the only place it is recorded.
      void analytics.trackTestSent(webhookId, {
        passed: false,
        response: getApiClientErrorDetailFromError(error),
      });
    }
  };

  return (
    <Box>
      <InlineStack alignBlock="flex-end" alignInline="space-between">
        <TextField
          data-test-webhook-endpoint
          label="Webhook endpoint"
          w="full"
          placeholder="https://"
          name="endpoint_url"
          value={endpointUrl}
          error={error}
          isDisabled={isDisabled}
          onInput={(event) => setEndpointUrl(event.target.value)}
          onBlur={(event) => setEndpointUrl(event.target.value)}
        />
        <Button
          data-test-webhook-send-test
          variant="secondary"
          mx="200"
          isDisabled={isSendTestDisabled}
          onClick={() => {
            void sendTest();
          }}
        >
          Send test
        </Button>
        <VerifyStatus
          isPending={verifyWebhook.isPending}
          isSuccess={verifyWebhook.isSuccess}
          isError={verifyWebhook.isError}
        />
      </InlineStack>
      {hasDuplicateEndpoint ? (
        <Banner
          variant="caution"
          icon={null}
          mt="150"
          description={DUPLICATE_ENDPOINT_NOTE}
          data-test-webhook-duplicate-endpoint
        />
      ) : null}
    </Box>
  );
}
